// ============================================================
// ChampAuto — un champ qui s'enregistre tout seul.
// ------------------------------------------------------------
// Il sert à DEUX écrans qui n'ont rien en commun : le formulaire du
// téléphone (`FeuilleAtelier`) et la colonne « réel » du bureau
// (`RetourAtelierPage`). Ils écrivent dans la même table, par deux
// routes différentes — l'une ouverte par jeton, l'autre authentifiée.
//
// C'est justement pour ça qu'il n'y en a qu'un. Deux implémentations de
// l'autosave finiraient par diverger sur le point qui compte : quand on
// écrit, et ce qu'on affiche quand l'écriture échoue. Et c'est toujours
// la plus laxiste des deux qui servirait.
// ============================================================

import { useEffect, useRef, useState } from 'react';

/** L'état d'un champ, en un mot. Un enregistrement muet ne se vérifie pas. */
const ETATS = {
  neutre: { texte: '', classe: '' },
  envoi: { texte: '…', classe: 'atelier__etat--envoi' },
  ok: { texte: '✓', classe: 'atelier__etat--ok' },
  echec: { texte: '✕ non enregistré', classe: 'atelier__etat--echec' },
};

/**
 * Un champ qui s'enregistre tout seul, à la sortie du champ.
 *
 * À la SORTIE et non à chaque frappe : « 45 » tapé caractère par
 * caractère enverrait « 4 » puis « 45 », et le 4 serait un temps réel
 * faux pendant une seconde — visible par le collègue qui regarde la même
 * feuille au même moment.
 *
 * @param {object} p
 * @param {boolean} [p.nu]  sans étiquette ni cadre : la cellule d'un
 *   tableau porte déjà son en-tête de colonne, une seconde étiquette y
 *   serait du bruit.
 */
export function ChampAuto({
  label,
  sousTitre,
  valeur,
  unite,
  type = 'number',
  nu = false,
  onEnregistrer,
}) {
  const [saisie, setSaisie] = useState(valeur ?? '');
  const [etat, setEtat] = useState('neutre');
  const initial = useRef(valeur ?? '');

  // La valeur venue du serveur gagne tant que l'utilisateur n'a pas
  // touché au champ : c'est ainsi que le second opérateur voit ce que le
  // premier a écrit.
  useEffect(() => {
    if (String(initial.current) === String(saisie)) {
      setSaisie(valeur ?? '');
      initial.current = valeur ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeur]);

  async function enregistrer() {
    if (String(saisie) === String(initial.current)) return;
    setEtat('envoi');
    try {
      await onEnregistrer(saisie === '' ? null : saisie);
      initial.current = saisie;
      setEtat('ok');
    } catch {
      setEtat('echec');
    }
  }

  const e = ETATS[etat];
  const champ = (
    <span className={`atelier__saisie${nu ? ' atelier__saisie--nu' : ''}`}>
      {/* La boîte (champ + unité) est un bloc à elle : c'est ELLE qu'on
          centre. Mettre l'état sur la même ligne décalerait le champ de
          la largeur réservée au mot « non enregistré » — visiblement, et
          d'autant plus que le mot est long. */}
      <span className="atelier__boite">
      <input
        className="atelier__input"
        // L'étiquette enveloppe déjà le champ quand elle existe, mais on
        // la répète en `aria-label` : sur un lecteur d'écran de
        // téléphone, une association implicite qui traverse deux `span`
        // n'est pas toujours restituée. En mode `nu`, c'est la SEULE
        // étiquette.
        aria-label={sousTitre ? `${label} — ${sousTitre}` : label}
        type={type}
        inputMode={type === 'number' ? 'decimal' : 'text'}
        min={type === 'number' ? 0 : undefined}
        value={saisie}
        onChange={(ev) => {
          setSaisie(ev.target.value);
          setEtat('neutre');
        }}
        onBlur={enregistrer}
      />
      {unite ? <span className="atelier__unite">{unite}</span> : null}
      </span>
      <span className={`atelier__etat ${e.classe}`}>{e.texte}</span>
    </span>
  );

  if (nu) return champ;

  return (
    <label className="atelier__champ">
      <span className="atelier__label">
        {label}
        {sousTitre ? <span className="atelier__sous">{sousTitre}</span> : null}
      </span>
      {champ}
    </label>
  );
}

export default ChampAuto;
